import {useEffect} from "react";

import * as Guide from "./api-impl.ts";

type CloseableProps = Guide.CloseableState & {
    /**
     * Callback to be invoked when the UX is closed.
     *
     * This is called when the mobile app requests that the UX be closed. On
     * calling this, the UX should be closed.
     */
    onClose: () => void | Promise<void>;
};

type ConfirmableProps = Guide.ConfirmableState & {
    /**
     * Callback to be invoked when the UX is cancelled.
     *
     * This is called when the mobile app requests that the UX cancel any
     * changes without submitting them. On calling this, the UX should be
     * cancelled without applying changes.
     */
    onCancel: () => void | Promise<void>;

    /**
     * Callback to be invoked when the UX is confirmed.
     *
     * This is called when the mobile app requests that the UX confirm any
     * changes and submit them. On calling this, the UX should be confirmed
     * and any changes applied, and then closed.
     */
    onConfirm: () => void | Promise<void>;
};

/**
 * Hook to simplify opting a modal UX component into the guide mobile UX
 * stack.
 *
 * When this hook mounts, it will signal to the mobile app that the UX is
 * open. When this hook unmounts, it will signal to the mobile app that the
 * UX is closed.
 *
 * If you need to have fine-grained control over these calls outside of the
 * mounting lifecycle, then the Guide calls `uxOpened`, `uxClosed` and
 * `onUXClose` should be used directly.
 *
 * @param slug The slug of the UX being managed. Only one component should
 * be rendering with this slug at any one time; multiple components cannot
 * manage the same slug and doing so will result in an error.
 * @param props Additional props to configure the hook.
 */
export const useUXStack = (
    slug: Guide.UXSlug,
    props: CloseableProps | ConfirmableProps,
): void => {
    // Split out the various props. Since we have props from two different
    // types, we need to do some type resolution.
    // We split these things out before the effect so that we don't have to
    // have the props object as a dependency in the effect, nor rely on callers
    // memoizing the props object and having that as a dependency in the effect.
    const onConfirm = "onConfirm" in props ? props.onConfirm : undefined;
    const onClose = "onCancel" in props ? props.onCancel : props.onClose;
    const canConfirm = "canConfirm" in props ? props.canConfirm : true;
    const translatedTitle = props.translatedTitle;
    const translatedConfirmLabel =
        "translatedConfirmLabel" in props
            ? props.translatedConfirmLabel
            : undefined;
    const translatedCancelLabel =
        "translatedCancelLabel" in props
            ? props.translatedCancelLabel
            : undefined;
    const translatedCloseLabel =
        "translatedCloseLabel" in props
            ? props.translatedCloseLabel
            : undefined;

    // This effect signals to the app integration that the UX is opened or
    // closed, and subscribes to the app integration telling us to close the UX
    // when opened. This assumes that being mounted means opened, and being
    // unmounted means closed. For more fine-grained usage outside of the mount
    // lifecylcle, use the Guide API support calls directly.
    // NOTE(somewhatabstact): The uxOpened and uxClosed calls are debounced to
    // ensure smooth app transitions. So, if this effect has to rerun because
    // something changes, but the outcome is that the UX is still open,
    // it should not cause the app integration experience to be janky.
    useEffect(() => {
        // On mount, tell the app integration we're opened...
        if (onConfirm != null) {
            Guide.uxOpened(slug, "confirm-or-cancel", {
                canConfirm,
                translatedTitle,
                translatedConfirmLabel,
                translatedCancelLabel,
            });
        } else {
            Guide.uxOpened(slug, "close", {
                translatedTitle,
                translatedCloseLabel,
            });
        }

        // ...and subscribe to the close UX signal. This will error if something
        // is already listening for this signal as we only support one
        // active UX per UX slug.
        const unsubscribe = Guide.onCloseUX(slug, async (action) => {
            if (action === "ok" && onConfirm != null && canConfirm) {
                await onConfirm();
            } else {
                await onClose();
            }
        });

        return () => {
            // On unmount, unsubscribe from the close UX signal...
            unsubscribe();

            // ...and tell the mobile app we're closed.
            Guide.uxClosed(slug);
        };
    }, [
        canConfirm,
        onClose,
        onConfirm,
        slug,
        translatedCancelLabel,
        translatedCloseLabel,
        translatedConfirmLabel,
        translatedTitle,
    ]);
};
